import {
  Button,
  color,
  InputGroup,
  InputSelect,
  SWIFT_CODE_REGEX,
} from "@funded-here-interface/common";
import { FC, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStyle } from "../../styles/BankDetailsCss";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RootState } from "../../store";
import { NO_REGEX } from "@funded-here-interface/common";
import { bankAccountTypes } from "../../constants/bank";
import useCreateOrg from "../../hooks/useCreateOrg";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import { getCode } from "country-list";
import { Industry, LegalStructure } from "../../constants/enums";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import {
  countryCodeAndCurrency,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import {
  initializeBankDetails,
  initializeBusinessDetails,
} from "../../Utils/dispatchISPV";

const DistributorBank: FC = () => {
  const { classes } = useStyle();
  const Navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const userId = useSelector((state: RootState) => state.auth.userId);
  const token = useSelector((state: RootState) => state.auth.token);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();
  const createOrgMutation = useCreateOrg();

  const [initPage, setInitPage] = useState(true);

  // Redux state
  const disBank = useSelector((state: RootState) => state.disBank);
  const disBiz = useSelector((state: RootState) => state.disBiz);

  // Form state
  const [form, setForm] = useState({
    bankName: "",
    accountNumber: "",
    accountType: "",
    swiftCode: "",
    accountHolderName: "",
  });

  // Error state
  const [error, setError] = useState({
    bankName: "",
    accountHolderName: "",
    bankAddress: "",
    swiftCode: "",
    accountNumber: "",
    accountType: "",
  });

  const dispatch = useDispatch();

  const populateFormFromDraft = useCallback(
    (data: any) => {
      setForm({
        bankName: data.bankName ?? "",
        accountType: data.accountType ?? "",
        accountNumber: data.accountNumber ?? "",
        accountHolderName: data.accountHolderName ?? "",
        swiftCode: data.swiftCode ?? "",
      });
    },
    [dispatch]
  );
  // Generic handler for input changes
  const handleFieldChange = useCallback(
    (field: keyof typeof form, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setError((prev) => ({ ...prev, [field]: "" }));
    },
    []
  );

  // Numeric input handler
  const handleNumericInputChange =
    (field: keyof typeof form) => (value: string) => {
      const numericValue = value.replace(NO_REGEX, "");
      handleFieldChange(field, numericValue);
    };

  // Initialize draft data on first page load
  useEffect(() => {
    if (!initPage) return;

    const initializeDraft = async () => {
      try {
        const params = {
          userId: parseInt(userId),
          token,
        };

        const data = await getOrgDraftMutation.mutateAsync(params);

        if (!data) return;

        populateFormFromDraft(data);

        initializeBusinessDetails(dispatch, data);
        initializeBankDetails(dispatch, data);
      } catch (error) {
        toast.error(`Error fetching draft: ${error}`);
      } finally {
        setInitPage(false);
      }
    };

    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save current form as draft
  const saveDraft = (showToast = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: Number(userId),
        token,
        ...form,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Handle back navigation
  const handleBack = () => {
    saveDraft(false);
    initializeBankDetails(dispatch, form);

    Navigate("/issuing-spv-biz-profile");
  };

  // Handle next (submit)
  const handleNext = async () => {
    saveDraft(false);
    const customError = { ...error };
    let valid = true;

    if (!form.bankName) {
      customError.bankName = "Bank Name is required";
      valid = false;
    }
    if (!form.accountNumber) {
      customError.accountNumber = "Account Number is required";
      valid = false;
    }
    if (!form.accountType) {
      customError.accountType = "Account Type is required";
      valid = false;
    }
    if (!form.swiftCode) {
      customError.swiftCode = "Swift Code is required";
      valid = false;
    }
    if (!form.accountHolderName) {
      customError.accountHolderName = "Account Holder Name is required";
      valid = false;
    }

    if (disBiz.company) {
      if (form.accountHolderName !== disBiz.company) {
        valid = false;
        toast.warn("Account Holder Name should be the same as company name");
      }
    }

    if (valid) {
      initializeBankDetails(dispatch, form);

      const bank = {
        bankName: form.bankName,
        swiftCode: form.swiftCode,
        accountNo: form.accountNumber,
        accountType: form.accountType.toLowerCase(),
        acountHolderName: form.accountHolderName,
      };

      let countryCode = null;
      if (disBiz.country && disBiz.country in countryCodeAndCurrency) {
        countryCode = countryNameToCodeMap.get(disBiz.country);
      } else {
        getCode(disBiz.country);
      }

      createOrgMutation.mutate(
        {
          bank,
          token,
          onBoardingUserType: Persona.DISTRIBUTOR,
          companyName: disBiz.company as string,
          companyRegistrationNo: disBiz.regNumber as string,
          countryISOCode: countryCode,
          countryOfIncorporation: disBiz.country as string,
          firstName: disBiz.userFirstName as string,
          lastName: disBiz.userLastName as string,
          industryGroup: disBiz.industryGroup as string,
          industry:
            disBiz.typeOfInstitute === Industry.OTHERS
              ? (disBiz.industryOthers as string)
              : (disBiz.typeOfInstitute as string),
          legalEntityAddress: disBiz.address as string,
          legalStructure:
            disBiz.legalStructure === LegalStructure.OTHERS
              ? (disBiz.legalStructureOthers as string)
              : (disBiz.legalStructure as string),
          postalCode: parseInt(`${disBiz.postal}`),
        },
        {
          onSuccess: () => {
            setIsLoading(false);
            Navigate("/thanks-page");
          },
          onError: (e) => {
            setIsLoading(false);
            toast.error((e as Error).message);
          },
        }
      );
    } else {
      setError(customError);
    }
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <InputGroup
        isErrored={error.bankName}
        onChange={(value: string) => handleFieldChange("bankName", value)}
        labelText="Bank Name"
        id="bank_name"
        value={form.bankName}
      />
      <InputGroup
        isErrored={error.accountNumber}
        onChange={handleNumericInputChange("accountNumber")}
        labelText="Bank Account Number"
        id="account_number"
        value={form.accountNumber}
      />
      <InputSelect
        isErrored={error.accountType}
        onChange={(value: string) => handleFieldChange("accountType", value)}
        labelText="Account type"
        id="accountType"
        value={form.accountType}
        data={bankAccountTypes}
      />
      <InputGroup
        isErrored={error.swiftCode}
        onChange={(value: string) =>
          handleFieldChange("swiftCode", value.replace(SWIFT_CODE_REGEX, ""))
        }
        labelText="Bank Swift Code"
        value={form.swiftCode}
        id="swift_code"
      />
      <BankAccNameDisclaimer />
      <InputGroup
        isErrored={error.accountHolderName}
        onChange={(value: string) =>
          handleFieldChange("accountHolderName", value)
        }
        labelText="Account Holder Name (should match with institution name)"
        value={form.accountHolderName}
        id="account_holder_name"
      />
      <div className={classes.buttonDiv}>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleBack}
            backgroundColor="#ffff"
            textColor="#338F86"
            children="Back"
            border="1px solid #338F86"
            width="100%"
          />
        </div>
        <ToastContainer />
        <div className={classes.buttonWrapper}>
          <Button
            disabled={isLoading}
            onClick={handleNext}
            textColor="#ffff"
            backgroundColor="#338F86"
            children="Submit"
            width="100%"
          />
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft()}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="118%"
        />
      </div>
    </div>
  );
};

export default DistributorBank;
